import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Observable, merge, of } from 'rxjs';
import { mergeMap, map, take } from 'rxjs/operators';
import {
  ContractActions,
  LOAD_PRODUCT_CONTRACT_COMPLETE,
  ContractLoadCompleteAction,
  LOAD_CONTRACTS_BALANCE,
  ContractsLoadBalanceAction,
  ContractsLoadBalanceCompleteAction,
} from '../../actions/contract.actions';
import { getAvailableContractsBalance } from '../../selectors/contract.selector';
import { Unit } from '@cemex-core/cmx-api/models/common/unit';
import { ContractBalance } from '../../models/contract-balance';
import { _safe } from '../../../../../shared/utils/safe-get.method';
import { CmxHttpClient } from '@cemex/cmx-client-layout';
import { OrderBuilderState } from '@ordersnproducts/modules/order-builder/reducers';

interface ContractBalanceResp {
  agreementItems: any[];
  count: number;
  links: {
    self: string;
  };
}

@Injectable()
export class ContractBalanceEffects {
  loadContracts: Observable<ContractActions> = createEffect(() =>
    this.actions$.pipe(
      ofType(LOAD_PRODUCT_CONTRACT_COMPLETE),
      map((action: ContractLoadCompleteAction) => {
        return new ContractsLoadBalanceAction(action.payload.salesDocuments.filter((contract) => contract.hasBalance));
      }),
    ),
  );

  contractBalanceLoad: Observable<ContractActions> = createEffect(() =>
    this.actions$.pipe(
      ofType(LOAD_CONTRACTS_BALANCE),
      mergeMap((action: ContractsLoadBalanceAction) => {
        const observables: Observable<ContractsLoadBalanceCompleteAction>[] = action.payload.map((contract) => {
          let volume: { amount: number; unitOfMeasure: Unit };
          return this.getBalance(contract.salesDocumentItemId).pipe(
            take(1),
            mergeMap((response: ContractBalanceResp) => {
              // Volume property does not exists in some contracts
              volume = _safe(() => response.agreementItems[0].volume.remaining.quantity);
              if (volume && volume.unitOfMeasure) {
                return this.getUnitDetail(volume.unitOfMeasure);
              } else {
                return of(null);
              }
            }),
            map((unitDetail: Unit) => {
              let remaining: { amount: number; unitOfMeasure: Unit };
              if (volume && unitDetail) {
                remaining = {
                  amount: volume && volume.amount, // Amount from balance
                  unitOfMeasure: { ...unitDetail }, // Unit detail from units
                };
              }
              return new ContractsLoadBalanceCompleteAction({
                [contract.salesDocumentItemId]: remaining,
              });
            }),
          );
        });

        return merge(...observables);
      }),
    ),
  );

  /**
   * To optimize http calls a little.
   * Almost every unit is id: 227 so we can retrieve the already fetched object
   * Instead of making the same call n(number of available contracts) times
   * @returns Already fetched and uniq-filtered array of units
   */
  private getFetchedUnits(contractsBalance: { [salesDocumentItemId: number]: ContractBalance }): Unit[] {
    const units: Unit[] = [];
    Object.keys(contractsBalance).forEach((key) => {
      const unit = contractsBalance[key] && contractsBalance[key].unitOfMeasure;
      if (unit !== undefined && !units.find((item) => item.unitId === unit.unitId)) {
        units.push(unit);
      }
    });

    return units;
  }

  private getUnitDetail(unitOfMeasure: Unit): Observable<Unit> {
    return this.store.select(getAvailableContractsBalance).pipe(
      take(1),
      mergeMap((contractsBalance) => {
        const sameUnit = this.getFetchedUnits(contractsBalance).find((unit) => unit.unitId === unitOfMeasure.unitId);
        if (sameUnit) {
          return of(sameUnit);
        } else {
          return this.http.get<Unit>((<any>unitOfMeasure).links.self).pipe(map((unit) => unit));
        }
      }),
    );
  }

  // Temp api call
  // -------------------------------------------------------------------
  private getBalance(agreementItemId: number): Observable<ContractBalanceResp> {
    return this.http.get(`/v2/qm/agreementitems/${agreementItemId}/balance`);
  }
  // -------------------------------------------------------------------

  constructor(
    private actions$: Actions,
    private store: Store<OrderBuilderState>,
    private http: CmxHttpClient,
  ) {}
}
